<script setup>
import { reactive, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

// Kredi ve hedef kitle bilgileri
const smsBalance = ref(null);
const audienceCount = ref(0);
const isLoadingCredits = ref(false);
const sendNow = ref(false);
const showPreview = ref(false);
const messageTextarea = ref(null);

// Kullanılabilir değişkenler
const availableVariables = [
  { key: '{{contact.name}}', label: '👤 İsim', preview: 'Ahmet Yılmaz' },
  { key: '{{contact.first_name}}', label: '📛 Ad', preview: 'Ahmet' },
  { key: '{{contact.last_name}}', label: '📛 Soyad', preview: 'Yılmaz' },
  { key: '{{contact.email}}', label: '📧 E-posta', preview: 'ahmet@example.com' },
  { key: '{{contact.phone_number}}', label: '📱 Telefon', preview: '+90 530 XXX XX XX' },
  { key: '{{contact.company_name}}', label: '🏢 Şirket', preview: 'ABC Ltd.' },
  { key: '{{contact.city}}', label: '🏙️ Şehir', preview: 'İstanbul' },
];

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getSMSInboxes'),
};

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  scheduledAt: null,
  selectedAudience: [],
};

const state = reactive({ ...initialState });

// Scheduled_at required sadece sendNow false ise
const rules = computed(() => ({
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  scheduledAt: sendNow.value ? {} : { required },
  selectedAudience: { required },
}));

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.SMS.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  message: getErrorMessage('message', 'MESSAGE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  scheduledAt: !sendNow.value ? getErrorMessage('scheduledAt', 'SCHEDULED_AT') : '',
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const isSubmitDisabled = computed(() => v$.value.$invalid);

// Mesaj önizlemesi - değişkenleri örnek değerlerle değiştir
const messagePreview = computed(() => {
  let preview = state.message;
  availableVariables.forEach(variable => {
    preview = preview.replace(new RegExp(variable.key.replace(/[{}]/g, '\\$&'), 'g'), variable.preview);
  });
  return preview;
});

// Değişken ekle
const insertVariable = (variableKey) => {
  state.message += variableKey;
};

// Kredi bilgisini çek
const fetchCredits = async () => {
  isLoadingCredits.value = true;
  try {
    const response = await fetch('/api/v1/credits/sms');
    if (response.ok) {
      const data = await response.json();
      smsBalance.value = data.balance;
    }
  } catch (e) {
    console.error('Failed to fetch credits', e);
  }
  isLoadingCredits.value = false;
};

// Hedef kitle sayısını hesapla
watch(() => state.selectedAudience, async (newAudience) => {
  if (newAudience && newAudience.length > 0) {
    audienceCount.value = newAudience.length * 100; // Placeholder
  } else {
    audienceCount.value = 0;
  }
}, { deep: true });

// Component mount olduğunda kredileri çek
fetchCredits();

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
  sendNow.value = false;
  showPreview.value = false;
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => ({
  title: state.title,
  message: state.message,
  inbox_id: state.inboxId,
  scheduled_at: sendNow.value ? new Date().toISOString() : formatToUTCString(state.scheduledAt),
  audience: state.selectedAudience?.map(id => ({
    id,
    type: 'Label',
  })),
});

const handleSubmit = async (immediate = false) => {
  sendNow.value = immediate;
  await v$.value.$validate();

  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

const handleSendNow = () => handleSubmit(true);
const handleSchedule = () => handleSubmit(false);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSchedule">
    <!-- Kredi Bilgisi Banner -->
    <div v-if="smsBalance !== null" class="p-3 rounded-lg bg-n-alpha-2 border border-n-slate-6">
      <div class="flex items-center justify-between">
        <span class="text-sm text-n-slate-11">📱 SMS Kredisi:</span>
        <span class="font-semibold text-n-green-text">{{ smsBalance?.toLocaleString() }} SMS</span>
      </div>
    </div>

    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.SMS.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.SMS.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <!-- Mesaj Alanı -->
    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.SMS.CREATE.FORM.MESSAGE.LABEL') }}
      </label>

      <!-- Değişken Etiketleri -->
      <div class="flex flex-wrap gap-1.5 mb-2">
        <button
          v-for="variable in availableVariables"
          :key="variable.key"
          type="button"
          class="px-2 py-1 text-xs rounded-md bg-n-blue-3 text-n-blue-text hover:bg-n-blue-4 transition-colors border border-n-blue-6"
          :title="`Örnek: ${variable.preview}`"
          @click="insertVariable(variable.key)"
        >
          {{ variable.label }}
        </button>
      </div>

      <TextArea
        ref="messageTextarea"
        v-model="state.message"
        :placeholder="t('CAMPAIGN.SMS.CREATE.FORM.MESSAGE.PLACEHOLDER')"
        show-character-count
        :message="formErrors.message"
        :message-type="formErrors.message ? 'error' : 'info'"
      />

      <!-- Önizleme Toggle -->
      <button
        v-if="state.message"
        type="button"
        class="mt-1 text-xs text-n-blue-text hover:underline text-left"
        @click="showPreview = !showPreview"
      >
        {{ showPreview ? '🔼 Önizlemeyi Gizle' : '🔽 Önizlemeyi Göster' }}
      </button>

      <!-- Mesaj Önizlemesi -->
      <div
        v-if="showPreview && state.message"
        class="mt-2 p-3 rounded-lg bg-n-green-2 border border-n-green-6"
      >
        <div class="text-xs text-n-green-11 mb-1 font-medium">📋 Mesaj Önizlemesi (Örnek Kişi):</div>
        <div class="text-sm text-n-green-12 whitespace-pre-wrap">{{ messagePreview }}</div>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.SMS.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.SMS.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <!-- Hedef Kitle Sayısı -->
      <div v-if="state.selectedAudience.length > 0" class="mt-1 text-xs text-n-slate-11">
        👥 Tahmini hedef kitle: <span class="font-semibold">~{{ audienceCount }} kişi</span>
      </div>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <!-- Butonlar -->
    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          variant="faded"
          color="slate"
          type="button"
          :label="t('CAMPAIGN.SMS.CREATE.FORM.BUTTONS.CANCEL')"
          class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
          @click="handleCancel"
        />
        <Button
          :label="t('CAMPAIGN.SMS.CREATE.FORM.BUTTONS.CREATE')"
          class="w-full"
          type="submit"
          :is-loading="isCreating"
          :disabled="isCreating || isSubmitDisabled"
        />
      </div>
      <!-- Hemen Gönder Butonu -->
      <Button
        variant="solid"
        color="green"
        type="button"
        label="⚡ Hemen Gönder"
        class="w-full"
        :is-loading="isCreating"
        :disabled="isCreating || !state.title || !state.message || !state.inboxId || state.selectedAudience.length === 0"
        @click="handleSendNow"
      />
    </div>
  </form>
</template>
